import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useSocketStore} from '~/stores/socket'
import {createLogger} from '~/utils/logger'

const logger = createLogger('OrderSync')

// Event names used throughout the order sync system
// These match the events published to Redis by the backend and broadcast via SocketCluster
const SYNC_EVENTS = {
    ORDER_SENT_TO_KITCHEN: 'pos:order:sent_to_kitchen',
    ORDER_ITEMS_ADDED: 'pos:order:items_added',
    ORDER_ITEMS_REMOVED: 'pos:order:items_removed',
    ORDER_STATUS_CHANGED: 'pos:order:status_changed',
    ORDER_ITEM_READY: 'pos:order:item_ready',
    ORDER_READY: 'pos:order:ready',
}

// Order lifecycle states
// Flow: draft -> sent_to_kitchen -> preparing -> ready -> completed
// Can be cancelled at any point before completed
const ORDER_STATUS = {
    DRAFT: 'draft',                     // Order being built, not yet sent to kitchen
    SENT_TO_KITCHEN: 'sent_to_kitchen', // Sent to kitchen, appears on KDS
    PREPARING: 'preparing',             // Kitchen is working on it (updated from KDS)
    READY: 'ready',                     // Food is ready for pickup/delivery
    READY_FOR_PICKUP: 'ready_for_pickup', // Order bumped from KDS, ready for customer pickup
    COMPLETED: 'completed',             // Payment received, order fulfilled
    CANCELLED: 'cancelled',             // Order was cancelled
}
let readyAudioInstance = null;

function getReadyAudio() {
    if (!readyAudioInstance) {
        readyAudioInstance = new Audio('/sounds/ready.mp3');
    }
    return readyAudioInstance;
}

// Shared subscription state across all component instances (singleton pattern)
// This ensures only ONE subscription is created even if useOrderSync() is called multiple times
const subscriptionState = reactive({
    isSubscribed: false,
    isSubscribing: false,
    channelName: null,
    channel: null,
    lastMessageTime: 0,
    lastPingTime: 0,
    lastPongTime: 0,
    lastPingLatency: 0,
    pingFailures: 0,
    subscriptionAttempts: 0,
    retryTimer: null,
    lastError: null,
    connectedSince: null
})

let sharedSyncInterval = null
let sharedHealthCheckInterval = null
// Refcount of mounted consumers of this composable. Without this, the
// FIRST instance to unmount called stopAutoSync() and cleared the shared
// interval — every other still-mounted instance silently lost auto-sync
// (orders pile up in unsynced state until next manual trigger). The
// counter only stops the interval when truly nobody needs it.
let mountedConsumerCount = 0

// This composable handles real-time order synchronization across multiple POS terminals
// Architecture: POS -> API endpoint -> Redis -> SocketCluster -> All subscribed clients
// Works in offline mode by saving to local DB first, then syncing to backend when available
export function useOrderSync() {
    const socketStore = useSocketStore()
    const userStore = useUserStore()
    const {isTabletMode} = useTabletMode()
    const {
        saveLocalOrder,
        getLocalOrder,
        updateLocalOrderStatus,
        deleteLocalOrder,
        syncAllPendingOrders,
        getUnsyncedOrders
    } = usePosDatabase()
    const {
        printKitchenReceipt,
        printIncrementalKitchenReceipt,
        printRemovedItemsKitchenReceipt,
        isMainPosTerminal,
        loadConfiguredPrinters,
        configuredPrinters
    } = usePrinter()

    const isConnected = computed(() => socketStore.isConnected && socketStore.isAuthenticated)
    const syncedOrders = ref(new Map())
    const pendingSyncs = ref([])
    const isSyncing = ref(false)
    const lastSyncTime = ref(null)
    const unsyncedCount = ref(0)

    let isCurrentlySyncing = false
    let readyAudio = null

    const calculateBackoff = (attempts) => {
        return Math.min(1000 * Math.pow(2, attempts), 30000)
    }

    const isSubscribed = computed(() => subscriptionState.isSubscribed)

    // Deprecated: We no longer broadcast directly via sockets
    // Instead, API endpoints publish to Redis, which SocketCluster then broadcasts
    // Kept for backwards compatibility but does nothing now
    const broadcastOrderUpdate = async (orderId, updateType, data = {}) => {
        logger.debug('Events are now broadcasted via API endpoints, not direct socket emit')
    }

    // Sends an order to the kitchen (makes it visible on KDS)
    // This is the main function called when staff clicks "Send to Kitchen"
    // Flow:
    // 1. Validate order exists and isn't already sent
    // 2. Try to sync to backend API (get real order number)
    // 3. Save to local DB (offline-first, works even if API fails)
    // 4. Print kitchen receipt (if this is the main POS terminal)
    // 5. Backend publishes event to Redis -> SocketCluster -> Other terminals get notified
    // Transform order to API format (backend expects old format)
    const transformOrderForAPI = (order) => {
        return {
            ...order,
            loyaltyCustomerId: order.loyaltyCustomerId || order.loyalty_customer_id || null,
            loyaltyRewardId: order.loyaltyRewardId || order.loyalty_reward_id || null,
            loyaltyRewardName: order.loyaltyRewardName || order.loyalty_reward_name || null,
            loyaltyRewardType: order.loyaltyRewardType || order.loyalty_reward_type || null,
            loyaltyRewardValue: order.loyaltyRewardValue || order.loyalty_reward_value || null,
            loyaltyPointsRedeemed: order.loyaltyPointsRedeemed || order.loyalty_points_redeemed || null,
            loyaltyRewardDiscount: order.loyaltyRewardDiscount || order.loyalty_reward_discount || 0,
            appliedDeals: order.appliedDeals || [],
            appliedDealId: order.appliedDeals?.[0]?.id || order.appliedDealId || null,
            appliedVoucherCode: order.appliedVoucherCode || null,
            items: (order.orderDetails || order.items).map(item => ({
                ...item,
                menuItemId: item.menuItemId,
                label: item.menuItemName,
                name: item.menuItemName,
                price: Number(item.unitPrice ?? item.price ?? 0),
                unitPrice: Number(item.unitPrice ?? item.price ?? 0),
                modifierPrice: (item.orderDetailModifiers || []).reduce((sum, m) => sum + Number(m.priceAdjustment || 0), 0),
                selectedModifierIds: (item.orderDetailModifiers || []).reduce((acc, mod) => {
                    const groupKey = mod.modifierGroupId
                    if (!groupKey || !mod.modifierGroupItemId) return acc
                    if (!acc[groupKey]) acc[groupKey] = []
                    acc[groupKey].push(mod.modifierGroupItemId)
                    return acc
                }, {}),
                // Preserve weight-based item fields
                weightSold: item.weightSold,
                weight_sold: item.weightSold,
                weightUnit: item.weightUnit,
                weight_unit: item.weightUnit,
                pricePerUnit: item.pricePerUnit,
                price_per_unit: item.pricePerUnit,
                isWeightBased: item.isWeightBased,
                is_weight_based: item.isWeightBased,
                // Misc item flag - ensures backend doesn't use item.id as menuItemId
                isMiscItem: item.isMiscItem || item.menuItemId === null
            }))
        }
    }

    const sendToKitchen = async (orderId) => {
        const {emitPosOrderSentToKitchen} = usePosEventBus()
        const {isTabletMode} = useTabletMode()

        try {
            logger.info('Sending order to kitchen:', orderId)

            const order = await getLocalOrder(orderId)
            if (!order) {
                logger.error('Order not found:', orderId)
                throw new Error('Order not found')
            }

            logger.debug('Order found, current status:', order.status)

            if (order.status === ORDER_STATUS.SENT_TO_KITCHEN || order.status === ORDER_STATUS.READY || order.status === ORDER_STATUS.COMPLETED) {
                logger.warn('Order already processed, status:', order.status)
                throw new Error(`Order already ${order.status}`)
            }

            const updatedOrder = {
                ...order,
                status: ORDER_STATUS.SENT_TO_KITCHEN,
                sentToKitchenAt: new Date().toISOString(),
            }

            logger.debug('Attempting to send to backend API')
            let apiSuccess = false
            let apiData = null
            try {
                const {getInstanceId} = usePosDatabase()
                const sourceInstanceId = await getInstanceId()

                // Transform to API format
                const apiPayload = transformOrderForAPI(updatedOrder)
                const {data, error} = await useApi(`/api/pos/orders/${orderId}/send-to-kitchen`, {
                    order: apiPayload,
                    businessId: userStore.activeBusinessId,
                    sourceDevice: isTabletMode.value ? 'tablet' : 'pos',
                    sourceInstanceId, // Unique instance ID for deduplication
                    guest_session_id: orderId, // Explicit POS local ID
                    order_id: order.backendOrderId || order.backend_order_id, // Backend ID if available
                }, {method: 'POST'})

                console.log('[sendToKitchen] Completed', data.value, error.value)
                if (error.value) {
                    logger.warn('Backend API error (will continue in offline mode):', error.value)
                } else {
                    logger.debug('Backend API response:', data.value)
                    apiSuccess = true
                    apiData = data.value

                    if (data.value.order) {
                        logger.debug('Using complete order object from backend')
                        // Merge backend order data, but preserve orderDetails if backend has them at top level
                        Object.assign(updatedOrder, data.value.order)

                        // Backend sends orderDetails at top level, not nested in order object
                        // if (data.value.orderDetails && data.value.orderDetails.length > 0) {
                        if (data.value.order.orderDetails && data.value.order.orderDetails.length > 0) {
                            updatedOrder.orderDetails = data.value.order.orderDetails
                            updatedOrder.items = data.value.order.orderDetails
                            logger.debug('Using orderDetails from top-level backend response')
                        }

                        updatedOrder.id = orderId // Preserve local ID
                        updatedOrder.posLocalId = orderId
                        updatedOrder.backendOrderId = data.value.backendOrderId
                        updatedOrder.backend_order_id = data.value.backendOrderId
                        updatedOrder.orderNumber = data.value.orderNumber
                        updatedOrder.syncedAt = new Date().toISOString()
                        updatedOrder.synced_at = new Date().toISOString()
                    }
                }
            } catch (apiErr) {
                logger.warn('Backend API failed (continuing in offline mode):', apiErr)
            }

            if (!apiSuccess) {
                logger.info('Operating in offline mode - order will sync when API is available')
            }

            logger.debug('Updating order status to SENT_TO_KITCHEN')
            await updateLocalOrderStatus(orderId, ORDER_STATUS.SENT_TO_KITCHEN)

            logger.debug('Saving updated order locally')
            await saveLocalOrder(updatedOrder)

            emitPosOrderSentToKitchen(orderId, updatedOrder.orderNumber)

            logger.debug('Updated order:', updatedOrder)

            if (configuredPrinters.value.length === 0) {
                logger.info('Loading configured printers before printing...')
                await loadConfiguredPrinters()
            }
            logger.debug('Configured printers:', configuredPrinters.value.length, configuredPrinters.value.map(p => ({name: p.friendly_name, dest: p.destination, enabled: p.is_enabled})))

            const isMainPos = await isMainPosTerminal()
            logger.debug('isMainPosTerminal result:', isMainPos)
            if (isMainPos) {
                logger.info('Printing kitchen receipt (Main POS Terminal)')
                try {
                    const printResult = await printKitchenReceipt(updatedOrder)
                    if (printResult.success) {
                        logger.success('Kitchen receipt printed successfully')

                        // Confirm print to backend - mark items as printed
                        if (apiSuccess && updatedOrder.backendOrderId) {
                            try {
                                const printedItemIds = (updatedOrder.orderDetails || updatedOrder.items || [])
                                        .map(item => item.id)
                                        .filter(Boolean)

                                if (printedItemIds.length > 0) {
                                    const {getInstanceId} = usePosDatabase()
                                    const sourceInstanceId = await getInstanceId()

                                    await useApi(`/api/pos/orders/${updatedOrder.backendOrderId}/confirm-print`, {
                                        orderDetailIds: printedItemIds,
                                        businessId: userStore.activeBusinessId,
                                        sourceDevice: isTabletMode.value ? 'tablet' : 'pos',
                                        sourceInstanceId,
                                        guest_session_id: orderId,
                                        order_id: updatedOrder.backendOrderId
                                    }, {method: 'POST'}, 2)

                                    logger.success('Confirmed print for', printedItemIds.length, 'items')

                                    // Update local order items with printed status
                                    const printedIdSet = new Set(printedItemIds)
                                    updatedOrder.orderDetails = updatedOrder.orderDetails.map(item => {
                                        if (printedIdSet.has(item.id)) {
                                            return {...item, itemStatus: 'printed', printedAt: new Date().toISOString()}
                                        }
                                        return item
                                    })
                                    updatedOrder.items = updatedOrder.orderDetails
                                    await saveLocalOrder(updatedOrder)
                                    logger.debug('Updated local order with printed status')
                                }
                            } catch (confirmError) {
                                logger.error('Failed to confirm print:', confirmError)
                            }
                        }
                    } else {
                        logger.warn('Kitchen printer not configured or print failed:', printResult.message)
                    }
                } catch (printErr) {
                    logger.warn('Kitchen print error (continuing anyway):', printErr)
                }
            } else {
                logger.debug('Skipping kitchen print (Not Main POS Terminal)')
            }

            logger.success('Order sent to kitchen successfully:', orderId)
            return {
                success: true,
                data: apiData,
                orderNumber: apiData?.orderNumber || updatedOrder.orderNumber,
                orderId: apiData?.backendOrderId || updatedOrder.backendOrderId,
                orderDetails: updatedOrder.orderDetails || updatedOrder.items || []
            }
        } catch (err) {
            logger.error('Failed to send order to kitchen:', err)
            return {success: false, error: err}
        }
    }

    // Adds items to an order that's already been sent to the kitchen
    // This happens when customers order more food after their initial order
    // For orders already READY, creates a new ticket on KDS instead of appending
    const addItemsToExistingOrder = async (orderId, newItems) => {
        const {emitPosOrderItemsAdded} = usePosEventBus()
        console.log('[addItemsToExistingOrder] CALLED with orderId:', orderId, 'newItems:', newItems)

        try {
            const localOrder = await getLocalOrder(orderId)
            if (!localOrder) {
                throw new Error('Order not found locally')
            }

            if (localOrder.status === ORDER_STATUS.COMPLETED) {
                throw new Error('Cannot modify completed/paid order')
            }

            // === INSERT LOG HERE ===
            console.log('[DEBUG-ITEMS] useOrderSync - addItemsToExistingOrder - localOrder.orderDetails BEFORE merge:', localOrder.orderDetails.length, localOrder.orderDetails.map(i => ({
                id: i.id,
                menuItemName: i.menuItemName,
                status: i.itemStatus,
                quantity: i.quantity
            })));
            console.log('[DEBUG-ITEMS] useOrderSync - newItems received for merge:', newItems.length, newItems.map(i => ({
                id: i.id,
                menuItemName: i.menuItemName,
                status: i.itemStatus,
                quantity: i.quantity
            })));
            // =======================

            // 1. Update the local order first (Offline-First)
            // Check if newItems are already in localOrder.orderDetails to avoid duplication
            // This can happen when processPayment saves cart to local DB before calling addItemsToExistingOrder
            const existingNames = new Set((localOrder.orderDetails || []).map(i =>
                `${i.menuItemId || 'misc'}_${i.menuItemName}_${i.unitPrice}_${i.quantity}`
            ));
            const itemsToAppend = newItems.filter(item => {
                const key = `${item.menuItemId || 'misc'}_${item.menuItemName}_${item.unitPrice}_${item.quantity}`;
                const alreadyExists = existingNames.has(key) && !item.id;
                if (alreadyExists) {
                    logger.debug(`Skipping duplicate item: ${item.menuItemName} (already in local order)`);
                }
                return !alreadyExists;
            });
            const updatedOrderDetails = [
                ...(localOrder.orderDetails || []),
                ...itemsToAppend.map(item => ({...item, itemStatus: 'printed'}))
            ];
            const updatedOrder = {
                ...localOrder,
                orderDetails: updatedOrderDetails,
                items: updatedOrderDetails, // ensure both are updated
                synced_at: null, // Mark for sync
            };
            await saveLocalOrder(updatedOrder);
            logger.success('Local order updated with new items.', {orderId, newItems});

            // === INSERT LOG HERE ===
            const latestLocalOrder = await getLocalOrder(orderId); // Re-fetch to confirm saved state
            console.log('[DEBUG-ITEMS] useOrderSync - localOrder.orderDetails AFTER merge and save (fetched):', latestLocalOrder.orderDetails.length, latestLocalOrder.orderDetails.map(i => ({
                id: i.id,
                menuItemName: i.menuItemName,
                status: i.itemStatus,
                quantity: i.quantity
            })));
            // =======================

            // 2. Emit the event so the local UI (KDS, new-order.vue) updates immediately
            emitPosOrderItemsAdded(orderId, newItems);

            // 3. Attempt to sync with the backend, but don't block/fail if offline
            try {
                const backendOrderId = localOrder.backendOrderId || localOrder.backend_order_id;
                if (!backendOrderId) {
                    logger.warn('No backend order ID. Sync will handle this order later.');
                    // The main sync process will eventually pick up this entire order
                    return {success: true, offline: true};
                }

                const {getInstanceId} = usePosDatabase();
                const sourceInstanceId = await getInstanceId();

                console.log('[addItemsToExistingOrder] Attempting to call API with backendOrderId:', backendOrderId, 'newItems:', newItems);

                const data = await useApi(`/api/pos/orders/${Number(backendOrderId)}/add-items`, {
                    newItems,
                    businessId: userStore.activeBusinessId,
                    orderType: localOrder.orderType,
                    currentStatus: localOrder.status,
                    kdsAction: localOrder.status === ORDER_STATUS.READY ? 'create_new_ticket' : 'append_to_ticket',
                    printerAction: 'print_incremental',
                    sourceDevice: isTabletMode.value ? 'tablet' : 'pos',
                    sourceInstanceId,
                    guest_session_id: orderId,
                    order_id: backendOrderId,
                }, {method: 'POST'}, 2);

                console.log('[addItemsToExistingOrder] API response:', data);

                if (data?.order) {
                    // To prevent data loss from an incomplete backend response,
                    // we update the local order with confirmation data rather than overwriting it.
                    const finalOrderState = await getLocalOrder(orderId);
                    if (finalOrderState) {
                        finalOrderState.synced_at = new Date().toISOString();
                        if (data.order.backendOrderId) {
                            finalOrderState.backendOrderId = data.order.backendOrderId;
                            finalOrderState.backend_order_id = data.order.backendOrderId;
                        }
                        if (data.order.orderNumber) finalOrderState.orderNumber = data.order.orderNumber;

                        // Also update the total amount from the backend, as it's the source of truth after calculation
                        if (data.order.totalAmount) finalOrderState.totalAmount = data.order.totalAmount;
                        if (data.order.subtotal) finalOrderState.subtotal = data.order.subtotal;

                        // Use backend's complete orderDetails as source of truth for IDs
                        // Match by index position: existing items keep their position, new items are appended
                        // Preserve local itemStatus for items that already had backend IDs
                        if (data.order.orderDetails) {
                            const existingItemCount = (localOrder.orderDetails || []).filter(item => item.id).length;
                            const localStatusMap = new Map(
                                (finalOrderState.orderDetails || [])
                                    .filter(item => item.id)
                                    .map(item => [item.id, item.itemStatus])
                            );

                            finalOrderState.orderDetails = data.order.orderDetails.map((backendItem, index) => {
                                const preservedStatus = localStatusMap.get(backendItem.id);
                                const isNewItem = index >= existingItemCount;
                                return {
                                    ...backendItem,
                                    itemStatus: preservedStatus || (isNewItem ? 'printed' : backendItem.itemStatus) || 'printed'
                                };
                            });
                            finalOrderState.items = finalOrderState.orderDetails;
                            logger.debug(`Merged ${data.order.orderDetails.length} backend items (${existingItemCount} existing, ${data.order.orderDetails.length - existingItemCount} new)`);
                        }

                        await saveLocalOrder(finalOrderState);
                        logger.debug('Synced local order with backend confirmation data');

                        // Return updated order details for the UI to update cart items
                        return {
                            success: true,
                            orderDetails: finalOrderState.orderDetails || finalOrderState.items || []
                        };
                    } else {
                        // Fallback to saving the backend order if the local one disappears for some reason
                        await saveLocalOrder(data.order);
                        return {
                            success: true,
                            orderDetails: data.order.orderDetails || data.order.items || []
                        };
                    }
                }
                logger.success('Incremental items synced to kitchen via API:', orderId);

            } catch (apiErr) {
                logger.warn('Failed to sync "add items" to backend (offline?). The order will sync later.', apiErr.message);
                // No need to throw, the local change is already saved and event emitted.
            }

            // Fetch final state to return to UI
            const finalOrder = await getLocalOrder(orderId);
            return {
                success: true,
                orderDetails: finalOrder?.orderDetails || finalOrder?.items || []
            };
        } catch (err) {
            logger.error('Failed to add items to order:', err);
            return {success: false, error: err};
        }
    }

    async function updateUnsyncedCount() {
        try {
            const unsynced = await getUnsyncedOrders()
            unsyncedCount.value = unsynced.length
        } catch (err) {
            logger.error('Failed to get unsynced count:', err)
        }
    }

    function isBrowserOffline() {
        return typeof navigator !== 'undefined' && navigator.onLine === false
    }

    async function triggerSync() {
        const {emitOrdersSynced, emitSyncFailed} = usePosEventBus()

        if (isTabletMode.value) {
            logger.debug('[Tablet Mode] Local order sync skipped - using direct API mode')
            return
        }

        if (isCurrentlySyncing) {
            logger.debug('Sync already in progress, skipping...')
            return
        }

        if (!socketStore.isConnected && isBrowserOffline()) {
            logger.warn('Browser is offline, skipping sync')
            return
        }

        if (!socketStore.isConnected) {
            logger.warn('Socket disconnected; attempting HTTP order sync anyway')
        }

        try {
            isCurrentlySyncing = true
            isSyncing.value = true

            logger.info('Starting automatic order sync...')
            const result = await syncAllPendingOrders()

            lastSyncTime.value = new Date().toISOString()
            await updateUnsyncedCount()

            logger.success(`Auto-sync completed: ${result.successCount} synced, ${result.failCount} failed, ${result.total} total`)

            emitOrdersSynced(result.successCount, result.failCount, result.total)

            return result
        } catch (err) {
            logger.error('Auto-sync error:', err)
            emitSyncFailed(err.message)
            return {successCount: 0, failCount: 0, total: 0}
        } finally {
            isCurrentlySyncing = false
            isSyncing.value = false
        }
    }

    function startAutoSync() {
        if (sharedSyncInterval) {
            logger.debug('Auto-sync already running')
            return
        }

        logger.info('Starting auto-sync monitor...')

        sharedSyncInterval = setInterval(async () => {
            try {
                await updateUnsyncedCount()

                if (unsyncedCount.value > 0 && (socketStore.isConnected || !isBrowserOffline())) {
                    await triggerSync()
                }
            } catch (error) {
                logger.error('Error in auto-sync interval:', error)
            }
        }, 30000)

        updateUnsyncedCount()
    }

    function stopAutoSync() {
        if (sharedSyncInterval) {
            clearInterval(sharedSyncInterval)
            sharedSyncInterval = null
            logger.info('Auto-sync monitor stopped')
        }
    }

    const subscribeToChannel = async () => {
        const {$sc} = useNuxtApp()

        if (subscriptionState.isSubscribed) {
            logger.debug('Already subscribed to channel, skipping')
            return
        }

        if (subscriptionState.isSubscribing) {
            logger.warn('Subscription attempt already in progress, skipping')
            return
        }

        if (subscriptionState.retryTimer) {
            clearTimeout(subscriptionState.retryTimer)
            subscriptionState.retryTimer = null
        }

        logger.socket('subscribeToChannel called', {
            attempt: subscriptionState.subscriptionAttempts + 1,
            isSubscribed: subscriptionState.isSubscribed,
            lastError: subscriptionState.lastError
        })

        const isSocketReady = $sc?.isReady() || (socketStore.isConnected && socketStore.isAuthenticated)

        if (!$sc) {
            logger.warn('Cannot subscribe - $sc not available')
            subscriptionState.lastError = '$sc not available'
            scheduleRetry()
            return
        }

        if (!isSocketReady) {
            logger.warn('Cannot subscribe - socket not ready', {
                scIsReady: $sc?.isReady(),
                storeConnected: socketStore.isConnected,
                storeAuthenticated: socketStore.isAuthenticated
            })
            subscriptionState.lastError = 'socket not ready'
            scheduleRetry()
            return
        }

        if (!userStore.activeBusinessId) {
            logger.warn('Cannot subscribe - no businessId')
            subscriptionState.lastError = 'no businessId'
            return
        }

        subscriptionState.isSubscribing = true

        try {
            logger.info('[useOrderSync] ⚠️ Subscription logic disabled - app.vue now handles all subscriptions (hub-and-spoke)')
            subscriptionState.isSubscribed = true
            subscriptionState.isSubscribing = false
            subscriptionState.connectedSince = Date.now()
            subscriptionState.lastMessageTime = Date.now()
        } catch (err) {
            subscriptionState.subscriptionAttempts++
            subscriptionState.lastError = err.message
            subscriptionState.isSubscribing = false
            logger.error('Subscription error:', err, {
                attempt: subscriptionState.subscriptionAttempts
            })
            scheduleRetry()
        }
    }

    const scheduleRetry = () => {
        if (!socketStore.isConnected || !socketStore.isAuthenticated) {
            logger.debug('Skipping retry - not connected/authenticated')
            return
        }

        const backoffDelay = calculateBackoff(subscriptionState.subscriptionAttempts)
        logger.info(`Scheduling retry in ${backoffDelay}ms (attempt ${subscriptionState.subscriptionAttempts + 1})`)

        subscriptionState.retryTimer = setTimeout(async () => {
            logger.info('Retrying subscription...')
            await subscribeToChannel()
        }, backoffDelay)
    }

    const checkSocketHealth = async () => {
        const {$sc} = useNuxtApp()

        if (!$sc?.socket) {
            logger.warn('Socket not available')
            subscriptionState.pingFailures++
            return false
        }

        const socketState = $sc.socket.state
        const isOpen = socketState === 'open'
        const isAuthenticated = !!$sc.socket.authToken
        const storeConnected = socketStore.isConnected
        const storeAuthenticated = socketStore.isAuthenticated

        const isHealthy = isOpen && isAuthenticated && storeConnected && storeAuthenticated

        if (!isHealthy) {
            subscriptionState.pingFailures++
            logger.warn('Socket unhealthy:', {
                socketState,
                isOpen,
                isAuthenticated,
                storeConnected,
                storeAuthenticated
            })
            return false
        }

        if (!subscriptionState.channel || !subscriptionState.isSubscribed) {
            subscriptionState.pingFailures++
            logger.warn('Channel not subscribed')
            return false
        }

        subscriptionState.pingFailures = 0
        subscriptionState.lastPongTime = Date.now()

        return true
    }

    const startHealthCheck = () => {
        if (sharedHealthCheckInterval) {
            clearInterval(sharedHealthCheckInterval)
        }

        sharedHealthCheckInterval = setInterval(async () => {
            try {
                if (!subscriptionState.isSubscribed) {
                    logger.debug('Skipping health check - not subscribed')
                    return
                }

                const isHealthy = await checkSocketHealth()

                if (!isHealthy) {
                    if (subscriptionState.pingFailures >= 3) {
                        logger.error('3 consecutive ping failures - forcing reconnection')
                        subscriptionState.isSubscribed = false
                        subscriptionState.subscriptionAttempts = 0
                        subscriptionState.pingFailures = 0
                        subscribeToChannel()
                    }
                } else {
                    const now = Date.now()
                    const timeSinceLastMessage = now - subscriptionState.lastMessageTime
                    const connectedDuration = Math.floor((now - subscriptionState.connectedSince) / 1000)

                    logger.debug(`Health check passed - connected ${connectedDuration}s, last msg ${Math.floor(timeSinceLastMessage / 1000)}s ago`)
                }
            } catch (error) {
                logger.error('Error in health check interval:', error)
            }
        }, 30000)
    }

    onMounted(async () => {
        const nuxtApp = useNuxtApp()
        const {$sc} = nuxtApp

        logger.info('onMounted called')
        logger.debug('$sc exists:', !!$sc)
        logger.debug('$sc.socket exists:', !!$sc?.socket)
        logger.debug('$sc.socket.state:', $sc?.socket?.state)
        logger.debug('Socket ready:', $sc?.isReady())
        logger.debug('socketStore.isConnected:', socketStore.isConnected)
        logger.debug('socketStore.isAuthenticated:', socketStore.isAuthenticated)
        logger.debug('Business ID:', userStore.activeBusinessId)
        logger.debug('Already subscribed:', isSubscribed.value)

        // Debug isReady components
        if ($sc) {
            logger.debug('isReady() breakdown:', {
                hasSocket: !!$sc.socket,
                socketState: $sc.socket?.state,
                isOpen: $sc.socket?.state === 'open',
                storeConnected: socketStore.isConnected,
                storeAuthenticated: socketStore.isAuthenticated,
                isReadyResult: $sc.isReady()
            })
        }

        // Try immediate subscription if ready
        if ($sc?.isReady() && userStore.activeBusinessId && !isSubscribed.value) {
            logger.info('Attempting immediate subscription...')
            await subscribeToChannel()
        } else {
            logger.debug('Skipping immediate subscription - waiting for socket ready')
            logger.debug('Reasons:', {
                hasSocket: !!$sc,
                socketReady: $sc?.isReady(),
                hasBusinessId: !!userStore.activeBusinessId,
                notSubscribed: !isSubscribed.value
            })
        }

        // Bump the refcount on every mount; startAutoSync is idempotent so
        // only the first mount actually creates the interval.
        mountedConsumerCount++
        startAutoSync()

        // Watch for socket readiness and auto-subscribe/unsubscribe
        // Watch the reactive properties that make up isReady()
        watch(
                () => ({
                    connected: socketStore.isConnected,
                    authenticated: socketStore.isAuthenticated,
                    hasBusinessId: !!userStore.activeBusinessId
                }),
                async (state, oldState) => {
                    const isReady = state.connected && state.authenticated && state.hasBusinessId
                    const wasReady = oldState?.connected && oldState?.authenticated && oldState?.hasBusinessId

                    logger.debug('State changed:', {
                        isReady,
                        wasReady,
                        isSubscribed: isSubscribed.value,
                        connected: state.connected,
                        authenticated: state.authenticated,
                        hasBusinessId: state.hasBusinessId,
                        oldConnected: oldState?.connected,
                        oldAuthenticated: oldState?.authenticated,
                        oldHasBusinessId: oldState?.hasBusinessId
                    })

                    if (isReady && !isSubscribed.value) {
                        logger.info('Socket ready, attempting subscription...')
                        // Small delay to ensure socket is fully ready
                        setTimeout(async () => {
                            await subscribeToChannel()
                        }, 500)
                    } else if (!isReady && isSubscribed.value) {
                        logger.warn('Socket disconnected, unsubscribing')
                        isSubscribed.value = false
                    } else if (isReady && isSubscribed.value) {
                        logger.debug('Already subscribed and ready')
                    } else {
                        logger.debug('Waiting for socket to be ready...')
                    }

                    if (isReady && !wasReady) {
                        logger.info('Socket reconnected, triggering sync...')
                        setTimeout(() => {
                            triggerSync()
                        }, 2000)
                    }
                },
                {deep: true, immediate: true}
        )
    })

    onBeforeUnmount(() => {
        // Decrement the refcount; only stop the shared interval when nobody
        // else is using it. Previously the first unmount killed the interval
        // for all still-mounted instances — auto-sync silently died.
        mountedConsumerCount = Math.max(0, mountedConsumerCount - 1)
        if (mountedConsumerCount === 0) {
            subscriptionState.isSubscribed = false
            if (subscriptionState.retryTimer) {
                clearTimeout(subscriptionState.retryTimer)
                subscriptionState.retryTimer = null
            }
            if (sharedHealthCheckInterval) {
                clearInterval(sharedHealthCheckInterval)
                sharedHealthCheckInterval = null
            }
            stopAutoSync()
        }
    })

    return {
        isConnected,
        syncedOrders,
        ORDER_STATUS,
        SYNC_EVENTS,
        broadcastOrderUpdate,
        sendToKitchen,
        addItemsToExistingOrder,
        isSyncing,
        lastSyncTime,
        unsyncedCount,
        triggerSync,
        startAutoSync,
        stopAutoSync,
        updateUnsyncedCount,
    }
}
